// @flow
import React, { useMemo, useCallback, type Node } from 'react';
import classNames from 'classnames';
import reduce from 'ramda/es/reduce';
import find from 'ramda/es/find';
import propEq from 'ramda/es/propEq';
import values from 'ramda/es/values';
import isEmpty from 'ramda/es/isEmpty';
import { Trans, useTranslation } from 'react-i18next';

import Modal, { ModalHeader, ModalFooter } from 'app/common/components/Modal';
import { Table, Tr, Td, Th } from 'app/common/ui-components/table';
import Loading from 'app/common/components/Loader';
import Picture from 'app/common/ui-next/picture';
import OverflowedText from 'app/common/components/OveflowedText';
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link';
import { useConfirmation } from 'app/common/components/Confirmation';
import usePopup from 'app/common/hooks/usePopup';
import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon';

import FileIcon from '../FileIcon';
import type { SolutionType, ProcessingFilesType, ProcessingFileType } from '../SolutionsData';
import { useVerificationsDataContext } from '../VerificationsData';
import Uploader from './Uploader';
import styles from './SolutionModal.scss';


const MAX_UPLOADED_FILES_COUNT = 5;

type Props = {
  onClose: () => void,
  onDeleteAttachedFile: (fileId: number, solutionId: number) => Promise<boolean>,
  onDeleteProcessingFile: (fileId: string) => void,
  onAddFiles: (accepted: Array<File>, rejected: Array<File>) => void,
  solution?: SolutionType,
  uploadingFiles: ProcessingFilesType,
  failedFiles: ProcessingFilesType,
  uploading?: boolean,
}

const SolutionModal = ({
  onClose,
  onDeleteAttachedFile,
  onDeleteProcessingFile,
  onAddFiles,
  solution,
  uploadingFiles,
  failedFiles,
  uploading,
}: Props): Node => {
  const { t } = useTranslation('dnevnik');

  const { id: solutionId, attached_files: attachedFiles = [] } = solution || {};

  const uploadingFilesArray = useMemo(() => values(uploadingFiles), [uploadingFiles]);
  const failedFilesArray = useMemo(() => values(failedFiles), [failedFiles]);

  const [verificationsBySolutionId] = useVerificationsDataContext();
  const verifications = verificationsBySolutionId[String(solutionId)] || [];
  const verificationsByFileId = useMemo(() => (
    reduce((acc, { attached_file_id: fileId, approved_at: approvedAt }) => ({
      ...acc,
      [String(fileId)]: approvedAt || 'not_approved',
    }), {}, verifications)
  ), [verifications]);

  const [showConfirmation, hideConfirmation, lockConfirmation] = useConfirmation();
  const popup = usePopup();
  const leftFilesCount = MAX_UPLOADED_FILES_COUNT - attachedFiles.length - uploadingFilesArray.length;


  const handleDelete = useCallback((fileId: number) => () => {
    const { name } = find(propEq('id', fileId), attachedFiles);

    const handleDeleteConfirm = async () => {
      lockConfirmation();
      const res = await onDeleteAttachedFile(fileId, solutionId);
      if (res) {
        popup(t('dnevnik_solution_modal_delete_file_success', 'Файл успешно удален'));
      }
      hideConfirmation();
    };

    showConfirmation({
      // `Удалить файл «${name}»?`
      message: t('dnevnik_solution_modal_delete_file_confirm_text', { name }),
      confirmButtonTitle: t('dnevnik_solution_modal_delete_file_confirm_delete_bt', 'Удалить'),
      cancelButtonTitle: t('dnevnik_solution_modal_delete_file_confirm_cancel_bt', 'Отмена'),
      onConfirm: handleDeleteConfirm,
    });
  }, [
    attachedFiles,
    hideConfirmation,
    lockConfirmation,
    showConfirmation,
    popup,
    onDeleteAttachedFile,
    solutionId,
    t,
  ]);


  const handleDeleteProcessing = useCallback((failedFileId: string) => () => {
    onDeleteProcessingFile(failedFileId);
  }, [onDeleteProcessingFile]);


  const loading = !solution;
  const empty = isEmpty(attachedFiles);
  const emptyUploading = isEmpty(uploadingFilesArray);
  const emptyFailed = isEmpty(failedFilesArray);
  const filesExist = !empty || !emptyUploading || !emptyFailed;


  return (
    <Modal toggleModal={onClose}>
      <ModalHeader>
        <Trans i18nKey="dnevnik:dnevnik_solution_modal_header">
          {'Прикрепленные файлы'}
        </Trans>
      </ModalHeader>

      {loading && <Loading />}

      {!loading
        && !filesExist
        && <div className={styles.uploaderNothingText}>
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_empty">
            {'Ни один файл еще не загружен'}
          </Trans>
        </div>}

      {!loading
        && filesExist
        && <Table className={styles.table}>
          <thead>
            <Tr header className={styles.tableHeader}>
              <Th alignLeft>
                {'№'}
              </Th>
              <Th alignLeft>
                <Trans i18nKey="dnevnik:dnevnik_solution_modal_column2">
                  {'Файл'}
                </Trans>
              </Th>
              <Th alignCenter>
                <Trans i18nKey="dnevnik:dnevnik_solution_modal_column3">
                  {'Формат'}
                </Trans>
              </Th>
              <Th alignCenter>
                <Trans i18nKey="dnevnik:dnevnik_solution_modal_column4">
                  {'Статус'}
                </Trans>
              </Th>
              <Th>
                &nbsp;
              </Th>
            </Tr>
          </thead>
          <tbody>
            {attachedFiles.map(({
              id: fileId,
              name,
              thumb_url: thumb,
              original_filetype: fType,
              original_filename: fName,
            }, i) => (
              <Tr key={fileId}>
                <Td className={styles.cellNum} alignLeft>{`${i + 1}.`}</Td>
                <Td className={styles.cellName}>
                  <OverflowedText>{name}</OverflowedText>
                </Td>
                <Td className={styles.cellThumb} alignCenter>
                  {thumb
                    ? <Picture
                      className={styles.thumb}
                      alt={name}
                      src={thumb}
                      height="40px"
                      width="40px"
                    />
                    : <FileIcon
                      type={fType}
                      name={fName}
                      className={styles.icon}
                    />}
                </Td>
                <Td alignCenter>
                  {verificationsByFileId[fileId]
                    ? <PlainIcon
                      type={iconTypes.CHECKED_DOUBLE}
                      width={20}
                      height="auto"
                    />
                    : <PlainIcon
                      type={iconTypes.CHECKED}
                      width={20}
                      height="auto"
                    />}
                </Td>
                <Td alignCenter>
                  {verificationsByFileId[fileId]
                    ? null
                    : <PlainButtonAsLink onClick={handleDelete(fileId)}>
                      <PlainIcon
                        type={iconTypes.CROSS_BOLD}
                        width={13}
                        height="auto"
                      />
                    </PlainButtonAsLink>}
                </Td>
              </Tr>
            ))}

            {uploadingFilesArray.map(({ id, name, type, uploadedPercent }: ProcessingFileType, i) => (
              <Tr key={id}>
                <Td className={styles.cellNum} alignLeft>{`${attachedFiles.length + i + 1}.`}</Td>
                <Td className={styles.cellName}>
                  <OverflowedText>{name}</OverflowedText>
                </Td>
                <Td className={styles.cellThumb} alignCenter>
                  <FileIcon
                    type={type}
                    name={name}
                    className={styles.icon}
                  />
                </Td>
                <Td alignCenter>
                  {uploadedPercent === -1
                    ? <PlainIcon
                      type={iconTypes.ERROR_CIRCLE}
                      width={20}
                      height="auto"
                    />
                    : `${uploadedPercent}%`}
                </Td>
                <Td alignCenter>
                  {uploadedPercent === -1
                    ? <PlainButtonAsLink onClick={handleDeleteProcessing(id)}>
                      <PlainIcon
                        type={iconTypes.CROSS_BOLD}
                        width={13}
                        height="auto"
                      />
                    </PlainButtonAsLink>
                    : ' '}
                </Td>
              </Tr>
            ))}

            {failedFilesArray.map(({ id, name, type }: ProcessingFileType) => (
              <Tr key={id}>
                <Td className={styles.cellNum} alignLeft>&nbsp;</Td>
                <Td className={styles.cellName}>
                  <OverflowedText>{name}</OverflowedText>
                </Td>
                <Td className={styles.cellThumb} alignCenter>
                  <FileIcon
                    type={type}
                    name={name}
                    className={styles.icon}
                  />
                </Td>
                <Td alignCenter>
                  <PlainIcon
                    type={iconTypes.ERROR_CIRCLE}
                    width={20}
                    height="auto"
                  />
                </Td>
                <Td alignCenter>
                  <PlainButtonAsLink onClick={handleDeleteProcessing(id)}>
                    <PlainIcon
                      type={iconTypes.CROSS_BOLD}
                      width={13}
                      height="auto"
                    />
                  </PlainButtonAsLink>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>}

      {leftFilesCount > 0
        ? <Uploader
          className={styles.uploader}
          maxFilesAvailable={MAX_UPLOADED_FILES_COUNT}
          leftFilesCount={leftFilesCount}
          onAddFiles={onAddFiles}
          disabled={uploading}
        />
        : <div className={classNames(styles.uploader, styles.uploaderMaximumText)}>
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_drop_area_maximum">
            {'Загружено максимально количество файлов'}
          </Trans>
        </div>}

      <ModalFooter className={styles.footer}>
        <span className={styles.legendItem}>
          <PlainIcon
            type={iconTypes.CHECKED}
            width={20}
            height="auto"
          />
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_dash">
            {' — '}
          </Trans>
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_uploaded">
            {'файл загружен'}
          </Trans>
        </span>

        <span className={styles.legendItem}>
          <PlainIcon
            type={iconTypes.CHECKED_DOUBLE}
            width={20}
            height="auto"
          />
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_dash">
            {' — '}
          </Trans>
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_checked">
            {'файл проверен'}
          </Trans>
        </span>

        <span className={styles.legendItem}>
          <PlainIcon
            type={iconTypes.ERROR_CIRCLE}
            width={20}
            height="auto"
          />
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_dash">
            {' — '}
          </Trans>
          <Trans i18nKey="dnevnik:dnevnik_solution_modal_legend_error">
            {'ошибка загрузки'}
          </Trans>
        </span>
      </ModalFooter>
    </Modal>
  );
};

export default SolutionModal;
